
import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronDown, Search, HelpCircle } from 'lucide-react';
import { useAccessibility } from '../hooks/useAccessibility';
import { ScrollReveal } from '../components/ScrollReveal';

const FAQ = () => {
  const { speak } = useAccessibility();
  const [searchTerm, setSearchTerm] = useState('');
  const [openQuestion, setOpenQuestion] = useState<number | null>(null);

  const faqs = [
    {
      categoria: 'Geral',
      perguntas: [
        {
          pergunta: 'Como funciona o app CONECTAR?',
          resposta: 'O CONECTAR é um aplicativo desenvolvido especialmente para crianças autistas que oferece ferramentas de comunicação alternativa, jogos educativos, desenho com IA, música personalizada e rotinas visuais. Cada funcionalidade é adaptada às necessidades individuais da criança.'
        },
        {
          pergunta: 'O CONECTAR é gratuito?',
          resposta: 'Sim! O CONECTAR oferece uma versão gratuita com recursos essenciais. Também temos planos premium com funcionalidades avançadas para famílias que desejam acesso completo a todas as ferramentas.'
        },
        {
          pergunta: 'Onde posso baixar o aplicativo?',
          resposta: 'No momento, o CONECTAR ainda não está disponível nas lojas de aplicativos. Ele poderá ser baixado na Google Play Store no futuro, e a instalação será gratuita.'
        }
      ]
    },
    {
      categoria: 'Segurança',
      perguntas: [
        {
          pergunta: 'O app é seguro para crianças?',
          resposta: 'Absolutamente! O CONECTAR foi desenvolvido seguindo as mais rigorosas normas de segurança infantil. Não coletamos dados pessoais desnecessários, não há anúncios e todo conteúdo é adequado para crianças. Os pais têm controle total sobre a experiência.'
        },
        {
          pergunta: 'Quais dados são coletados?',
          resposta: 'Coletamos apenas dados essenciais para personalizar a experiência da criança, como preferências de atividades e progresso no aprendizado. Todos os dados são criptografados e jamais compartilhados com terceiros.'
        }
      ]
    },
    {
      categoria: 'Funcionalidades',
      perguntas: [
        {
          pergunta: 'O app funciona offline?',
          resposta: 'Sim! Muitas funcionalidades do CONECTAR funcionam offline, incluindo jogos básicos, desenho e comunicação por cards. Para recursos com IA (reconhecimento de desenhos e música personalizada), é necessária conexão com internet.'
        },
        {
          pergunta: 'Como personalizar o perfil da criança?',
          resposta: 'No primeiro acesso, você pode criar um perfil personalizado escolhendo nome, avatar ou foto da criança. Durante o uso, o app aprende as preferências e adapta automaticamente as atividades ao nível de desenvolvimento.'
        }
      ]
    },
    {
      categoria: 'Suporte Educacional',
      perguntas: [
        {
          pergunta: 'O app tem suporte para escolas?',
          resposta: 'Ainda não. O app não tem suporte para escolas no momento, mas esse recurso está previsto para quando o CONECTAR chegar à Google Play Store.'
        },
        {
          pergunta: 'Professores precisam de treinamento?',
          resposta: 'No momento, não oferecemos treinamento. O app foi pensado para ser intuitivo e de uso simples.'
        },
        {
          pergunta: 'Como acompanhar o progresso da criança?',
          resposta: 'O app gera relatórios automáticos mostrando evolução em comunicação, coordenação motora, reconhecimento de formas e letras. Pais e educadores recebem insights semanais sobre o desenvolvimento.'
        }
      ]
    },
    {
      categoria: 'Técnico',
      perguntas: [
        {
          pergunta: 'Quais dispositivos são compatíveis?',
          resposta: 'No momento, o CONECTAR funciona apenas em smartphones e tablets Android, a partir da versão 6.0.'
        },
        {
          pergunta: 'O app consome muita bateria?',
          resposta: 'Não, o CONECTAR foi otimizado para consumo eficiente de energia. Funcionalidades com IA podem usar um pouco mais de bateria, mas implementamos técnicas de otimização para minimizar o impacto.'
        },
        {
          pergunta: 'Como reportar problemas técnicos?',
          resposta: 'Você pode reportar problemas através do próprio app (menu Ajuda) ou por email (gp.ticam@ifap.edu.br). Nossa equipe responde em até 24 horas.'
        }
      ]
    }
  ];

  const filteredFAQs = faqs.map(categoria => ({
    ...categoria,
    perguntas: categoria.perguntas.filter(faq =>
      faq.pergunta.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.resposta.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(categoria => categoria.perguntas.length > 0);

  const toggleQuestion = (index: number) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-16 pb-14 md:pt-20 md:pb-16 bg-background overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.35] dark:opacity-[0.12]"
          style={{ backgroundImage: 'radial-gradient(rgb(var(--primary)) 1px, transparent 1px)', backgroundSize: '26px 26px' }}
          aria-hidden="true"
        />
        <div
          className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgb(var(--primary)) 0%, transparent 70%)' }}
          aria-hidden="true"
        />

        <div className="relative container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-primary/10 dark:bg-primary/15 px-3 py-1.5 rounded-full text-sm font-semibold text-foreground mb-6">
              <HelpCircle className="w-4 h-4 text-primary-ink dark:text-primary-tint" />
              FAQ
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-[1.1]">
              Perguntas <span className="text-primary">Frequentes</span>
            </h1>

            <p className="font-body text-lg md:text-xl text-muted-foreground leading-relaxed mb-8">
              Encontre respostas rápidas para as dúvidas mais comuns sobre o CONECTAR.
              Não encontrou o que procura? Entre em contato conosco!
            </p>

            {/* Busca */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar pergunta..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <ScrollReveal>
      <section className="py-20">
        <div className="container mx-auto px-4">
          {filteredFAQs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl text-muted-foreground">
                Nenhuma pergunta encontrada. Tente uma busca diferente ou 
                <span className="text-primary cursor-pointer hover:underline ml-1">
                  entre em contato conosco
                </span>.
              </p>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              {filteredFAQs.map((categoria, categoriaIndex) => (
                <div key={categoriaIndex} className="mb-8">
                  <h2 className="text-2xl font-bold text-foreground mb-6 border-b border-border pb-2">
                    {categoria.categoria}
                  </h2>
                  
                  <div className="space-y-4">
                    {categoria.perguntas.map((faq, faqIndex) => {
                      const globalIndex = categoriaIndex * 100 + faqIndex; // Unique index
                      const isOpen = openQuestion === globalIndex;
                      
                      return (
                        <div key={globalIndex} className="rounded-2xl bg-primary/[0.05] dark:bg-primary/[0.1] overflow-hidden">
                          <button
                            onClick={() => {
                              toggleQuestion(globalIndex);
                              speak(isOpen ? 'Fechar resposta' : faq.pergunta);
                            }}
                            className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-secondary/50 transition-colors"
                          >
                            <span className="font-medium text-foreground pr-4">{faq.pergunta}</span>
                            <ChevronDown 
                              className={`w-5 h-5 text-muted-foreground transition-transform flex-shrink-0 ${
                                isOpen ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                          
                          {isOpen && (
                            <div className="px-6 pb-4">
                              <div className="pt-2 border-t border-border">
                                <p className="text-muted-foreground leading-relaxed">
                                  {faq.resposta}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      </ScrollReveal>

      {/* Ainda com dúvidas */}
      <ScrollReveal>
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Ainda tem dúvidas?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Nossa equipe de suporte está sempre pronta para ajudar você. 
            Entre em contato através dos nossos canais de atendimento.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 rounded-lg font-medium transition-colors"
              onClick={() => speak('Ir para página de contato')}
            >
              Fale Conosco
            </button>
          </div>
        </div>
      </section>
      </ScrollReveal>

      <Footer />
    </div>
  );
};

export default FAQ;
